/**
 * Reading colour off pictures, for the mood board
 * -------------------------------------------------------------
 * Two things come from it, both free and in the browser: the order the
 * supporting pictures are laid in (by hue, so neighbours shade into each
 * other) and the board's colour strip.
 *
 * Library pictures are mostly pack shots on white, so the near-white and
 * near-black of a backdrop are left out of every count -- otherwise every
 * board's palette would open with white.
 *
 * A picture whose server does not allow it to be read (no CORS) simply has
 * no colour; the board is still made, in the order it was given.
 */

export interface PictureColour {
  hex: string
  /** 0-360, or -1 when the picture is too grey to have a hue worth ordering by. */
  hue: number
  light: number
  /** Up to 400 of its pixels, for the board's palette. */
  sample: [number, number, number][]
}

const SIDE = 40

function toHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('')
}

function hsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const R = r / 255
  const G = g / 255
  const B = b / 255
  const max = Math.max(R, G, B)
  const min = Math.min(R, G, B)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = max === R ? (G - B) / d + (G < B ? 6 : 0) : max === G ? (B - R) / d + 2 : (R - G) / d + 4
  h *= 60
  return { h, s, l }
}

export function readColour(url: string): Promise<PictureColour | null> {
  return new Promise((resolve) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.decoding = 'async'
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = SIDE
        canvas.height = SIDE
        const pen = canvas.getContext('2d', { willReadFrequently: true })
        if (!pen) return resolve(null)
        pen.drawImage(image, 0, 0, SIDE, SIDE)
        const data = pen.getImageData(0, 0, SIDE, SIDE).data
        let r = 0
        let g = 0
        let b = 0
        let n = 0
        const sample: [number, number, number][] = []
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] < 128) continue
          const { s, l } = hsl(data[i], data[i + 1], data[i + 2])
          // Backdrop: white, black, and the greys right next to them.
          if (l > 0.92 || l < 0.05 || (s < 0.06 && l > 0.85)) continue
          r += data[i]
          g += data[i + 1]
          b += data[i + 2]
          n += 1
          if (sample.length < 400 && i % 16 === 0) sample.push([data[i], data[i + 1], data[i + 2]])
        }
        if (n === 0) return resolve({ hex: '#f2f2f2', hue: -1, light: 0.95, sample: [] })
        r /= n
        g /= n
        b /= n
        const { h, s, l } = hsl(r, g, b)
        resolve({ hex: toHex(r, g, b), hue: s < 0.12 ? -1 : h, light: l, sample })
      } catch {
        // Tainted: the server did not allow reading it.
        resolve(null)
      }
    }
    image.onerror = () => resolve(null)
    image.src = url
  })
}

/**
 * Supporting pictures in an order that flows by colour: the coloured ones
 * round the colour wheel, starting from the lead picture's hue, then the
 * neutral ones from dark to light. The lead stays first.
 */
export function orderByColour<T>(items: T[], colourOf: (item: T) => PictureColour | null | undefined): T[] {
  if (items.length <= 2) return items
  const [lead, ...rest] = items
  const start = colourOf(lead)?.hue ?? -1
  const from = start >= 0 ? start : 0
  const coloured = rest.filter((item) => (colourOf(item)?.hue ?? -1) >= 0)
  const neutral = rest.filter((item) => (colourOf(item)?.hue ?? -1) < 0)
  coloured.sort((a, b) => ((colourOf(a)!.hue - from + 360) % 360) - ((colourOf(b)!.hue - from + 360) % 360))
  neutral.sort((a, b) => (colourOf(a)?.light ?? 0.5) - (colourOf(b)?.light ?? 0.5))
  return [lead, ...coloured, ...neutral]
}

/**
 * The board's colour strip: a few colours that between them stand for all its
 * pictures, light to dark. k-means over the pictures' pixels, seeded the same
 * way every time so the strip does not change on its own.
 */
export function boardPalette(colours: (PictureColour | null | undefined)[], count = 5): string[] {
  const points = colours.flatMap((colour) => colour?.sample ?? [])
  if (points.length < count) return colours.filter(Boolean).map((colour) => colour!.hex).slice(0, count)
  // Seeds spread through the points sorted by lightness.
  const sorted = [...points].sort((a, b) => a[0] + a[1] + a[2] - (b[0] + b[1] + b[2]))
  let centres = Array.from({ length: count }, (_, i) => sorted[Math.floor(((i + 0.5) / count) * sorted.length)].slice() as number[])
  for (let round = 0; round < 12; round += 1) {
    const sums = centres.map(() => [0, 0, 0, 0])
    for (const p of points) {
      let best = 0
      let bestD = Infinity
      centres.forEach((c, i) => {
        const d = (p[0] - c[0]) ** 2 + (p[1] - c[1]) ** 2 + (p[2] - c[2]) ** 2
        if (d < bestD) {
          bestD = d
          best = i
        }
      })
      sums[best][0] += p[0]
      sums[best][1] += p[1]
      sums[best][2] += p[2]
      sums[best][3] += 1
    }
    centres = sums.map((s, i) => (s[3] > 0 ? [s[0] / s[3], s[1] / s[3], s[2] / s[3]] : centres[i]))
  }
  return centres
    .sort((a, b) => hsl(b[0], b[1], b[2]).l - hsl(a[0], a[1], a[2]).l)
    .map((c) => toHex(c[0], c[1], c[2]))
}
